import {useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';
import type {GridSortModel} from '@mui/x-data-grid';
import {DEFAULT_PAGE, DEFAULT_PAGE_SIZE} from '../constants';
import {getOrdering} from '../utils';

const PAGINATION_PARAMS = ['page', 'pageSize', 'ordering'];

interface UsePaginationParamsProps {
  defaultSortModel: GridSortModel;
}

interface UsePaginationParamsResult {
  page: number;
  setPage: (newPage: number) => void;
  pageSize: number;
  setPageSize: (newPageSize: number) => void;
  ordering: string;
  setOrdering: (newSortModel: GridSortModel) => void;
  filters: {[key: string]: string};
  setFilters: (newFilters: Record<string, unknown>) => void;
}

export const usePaginationParams = ({
  defaultSortModel,
}: UsePaginationParamsProps): UsePaginationParamsResult => {
  const [searchParams, setSearchParams] = useSearchParams();

  const pageParam = searchParams.get('page');
  const page = pageParam ? parseInt(pageParam) : DEFAULT_PAGE;

  const pageSizeParam = searchParams.get('pageSize');
  const pageSize = pageSizeParam ? parseInt(pageSizeParam) : DEFAULT_PAGE_SIZE;

  const ordering =
    searchParams.get('ordering') || getOrdering(defaultSortModel);

  // All other params that are not pagination related
  const filters = useMemo(() => {
    const result: Record<string, string> = {};
    for (const [key, value] of searchParams.entries()) {
      if (!PAGINATION_PARAMS.includes(key)) {
        result[key] = value;
      }
    }

    return result;
  }, [searchParams]);

  // Update or remove params while keeping other values
  const updateParams = (updates: Record<string, unknown>): void => {
    const newParams = new URLSearchParams(searchParams);

    for (const [key, value] of Object.entries(updates)) {
      if (value === null && newParams.has(key)) {
        newParams.delete(key);
      } else {
        newParams.set(key, String(value));
      }
    }

    setSearchParams(newParams);
  };

  const setPage = (newPage: number) => {
    updateParams({page: newPage});
  };

  const setPageSize = (newPageSize: number) => {
    updateParams({pageSize: newPageSize, page: DEFAULT_PAGE});
  };

  const setOrdering = (newSortModel: GridSortModel) => {
    if (newSortModel.length === 0) {
      updateParams({ordering: null, page: DEFAULT_PAGE});
      return;
    }

    const newOrdering = getOrdering(newSortModel);
    updateParams({ordering: newOrdering, page: DEFAULT_PAGE});
  };

  const setFilters = (newFilters: Record<string, unknown>) => {
    updateParams({
      ...filters,
      ...newFilters,
    });
  };

  return {
    page,
    setPage,
    pageSize,
    setPageSize,
    ordering,
    setOrdering,
    filters,
    setFilters,
  };
};
